import { ChangeDetectionStrategy, Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IStorage, LocalStorage } from '@isophi/core-legacy';
import { ManagerTeacher, TeachersService } from '@isophi/mng-api';
import { map, Observable, startWith, Subject, switchMap } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs';

import { HelperService } from '../../../../core/services/helper.service';
import { BaseComponent } from '../../../../shared/components/base/base.component';
import { LocalStorageConstService } from '../../../../shared/services/local-storage-const.service';
import { autoEvaluationStateData, AutoEvaluationStateEnum } from '../../constants/data';
import { AutoEvaluationFilterForm, AutoEvaluationFilterFormValue } from '../../constants/forms';
import { AutoEvaluationFilterCriteria } from '../../constants/models';

@Component({
  selector: 'app-pap-autoevaluation-filter',
  templateUrl: './pap-autoevaluation-filter.component.html',
  styleUrls: ['./pap-autoevaluation-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PapAutoevaluationFilterComponent extends BaseComponent implements OnInit {
  @Output() filterUpdated = new EventEmitter<AutoEvaluationFilterCriteria>();
  @Output() export = new EventEmitter<'xlsx' | 'csv'>();

  private readonly lSConstService = inject(LocalStorageConstService);
  private readonly teacherService = inject(TeachersService);
  private readonly helperService = inject(HelperService);
  private readonly storage: IStorage = new LocalStorage();

  autoEvaluationStateData = this.helperService.prepareData(autoEvaluationStateData, AutoEvaluationStateEnum);

  fetchTeachers$ = new Subject<string>();

  teachers$: Observable<ManagerTeacher[]> = this.fetchTeachers$.pipe(
    startWith(undefined),
    switchMap((search) => this.teacherService.teachersList(undefined, undefined, undefined, undefined, search)),
    takeUntil(this.ngUnsubscribe$),
    map((teachers) =>
      teachers.results.map((teacher) => ({
        ...teacher,
        fullName: `${teacher.firstName} ${teacher.lastName}`,
      }))
    )
  );

  filterForm = new FormGroup<AutoEvaluationFilterForm>({
    teacherId: new FormControl<number | null>(null),
    state: new FormControl<string | null>(null),
  });

  ngOnInit(): void {
    this.filterForm.valueChanges.pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$)).subscribe((filterFormValue) => {
      this.filterPortfolioActivities(filterFormValue as AutoEvaluationFilterFormValue);
    });
    if (this.storage.hasItem(this.lSConstService.papAutoEvaluationFilter)) {
      const storageData = this.storage.getItem(this.lSConstService.papAutoEvaluationFilter);
      if (storageData && storageData !== 'undefined') {
        const filterCriteria: AutoEvaluationFilterCriteria = JSON.parse(storageData);
        this.filterForm.patchValue({
          teacherId: filterCriteria.teacherId,
          state: filterCriteria.state,
        });
      }
    }
  }

  exportPage(): void {
    this.export.emit('xlsx');
  }

  filterPortfolioActivities(filterFormValue: AutoEvaluationFilterFormValue): void {
    const filterCriteria: AutoEvaluationFilterCriteria = {
      teacherId: filterFormValue.teacherId,
      state: filterFormValue.state,
    };
    this.storage.setItem(this.lSConstService.papPortfolioFilter, JSON.stringify(filterCriteria));
    this.filterUpdated.emit(filterCriteria);
  }
}
